import { useContext, useEffect, useRef, useState } from 'react';
import MlxBlockUI from '../../../components/common/MlxBlockUI/MlxBlockUI';
import withSidebar from '../../../hoc/withSideBar/withSideBar';
import { AuthCtx } from '../../../context/AuthCtxProvider';
import { _S3ImageResponse, _ToastMessageProps, _UserType } from '../../../constants/staticTypes';
import { AlertVariant, IMAGE_TYPE } from '../../../constants/constants';
import {
    ADMIN_STAFF,
    CANCEL,
    CONFIRM_PASSWORD,
    DISPLAY_NAME,
    EMAIL,
    ENTER_NEW_PASSWORD_MESSAGE,
    FIRST_NAME,
    HIRE_DATE,
    LAST_NAME,
    LOGOUT,
    MY_PROFILE,
    NEW_PASSWORD,
    OLD_PASSWORD,
    ORG_ADMIN,
    ORG_STAFF,
    PASSWORD_CHECK_LOWERCASE,
    PASSWORD_CHECK_MIN,
    PASSWORD_CHECK_NUMBER,
    PASSWORD_CHECK_SPECIAL,
    PASSWORD_CHECK_UPPERCASE,
    PERSONAL_INFORMATION,
    PHONE,
    RESET_PASSWORD,
    RESET_PASSWORD_PROFILE_TITLE,
    SUPER_ADMIN,
    UPDATE,
} from '../../../constants/strings';
import { useTranslation } from '../../../hooks/useTranslation';
import { InputText } from 'primereact/inputtext';
import './orgProfile.scss';
import { useFormik } from 'formik';
import { checkPasswordCriteria } from '../../../helpers/registerAccountHelper';
import { Button } from 'primereact/button';
import { Messages } from 'primereact/messages';
import MlxButton from '../../../components/common/MlxButton/MlxButton';
import dayjs from 'dayjs';
import { useMutation } from 'react-query';
import HttpServiceHelper from '../../../helpers/httpServiceHelper';
import {
    ChangePasswordSchema,
    initialChangePasswordFormData,
    orgStaffChangeNewPassword,
    updateOrgProfilePicture,
} from '../../../helpers/authHelper';
import { defaultToastData } from '../../../helpers/commonHelper';
import MlxToast from '../../../components/common/MlxToast/MlxToast';
import MlxImageUpload from '../../../components/common/MlxImageUpload/MlxImageUpload';
import ChangeEmailModal from './components/ChangeEmailModal';

const languageTexts = {
    LOGOUT: LOGOUT,
    SUPER_ADMIN: SUPER_ADMIN,
    ADMIN_STAFF: ADMIN_STAFF,
    ORG_ADMIN: ORG_ADMIN,
    ORG_STAFF: ORG_STAFF,
    MY_PROFILE: MY_PROFILE,
    DISPLAY_NAME: DISPLAY_NAME,
    FIRST_NAME: FIRST_NAME,
    LAST_NAME: LAST_NAME,
    EMAIL: EMAIL,
    PHONE: PHONE,
    HIRE_DATE: HIRE_DATE,
    NEW_PASSWORD: NEW_PASSWORD,
    OLD_PASSWORD: OLD_PASSWORD,
    CONFIRM_PASSWORD: CONFIRM_PASSWORD,
    RESET_PASSWORD: RESET_PASSWORD,
    ENTER_NEW_PASSWORD_MESSAGE: ENTER_NEW_PASSWORD_MESSAGE,
    PASSWORD_CHECK_LOWERCASE: PASSWORD_CHECK_LOWERCASE,
    PASSWORD_CHECK_UPPERCASE: PASSWORD_CHECK_UPPERCASE,
    PASSWORD_CHECK_NUMBER: PASSWORD_CHECK_NUMBER,
    PASSWORD_CHECK_MIN: PASSWORD_CHECK_MIN,
    PASSWORD_CHECK_SPECIAL: PASSWORD_CHECK_SPECIAL,
    CANCEL: CANCEL,
    UPDATE: UPDATE,
    PERSONAL_INFORMATION: PERSONAL_INFORMATION,
    RESET_PASSWORD_PROFILE_TITLE: RESET_PASSWORD_PROFILE_TITLE,
};

function OrgProfile() {
    const { userType, userData, setUserData } = useContext(AuthCtx);
    const profileImageInitialValue = {
        imageKey: '',
        imageUrl: null, // need to get the image url using key
    };
    const translatedText = useTranslation(languageTexts, 'capitalize');
    const messageRef = useRef<Messages>(null);
    const changePasswordMutation = useMutation(HttpServiceHelper);
    const changeProfileImageMutation = useMutation(HttpServiceHelper);

    const [profileImage, setProfileImage] = useState<_S3ImageResponse>(profileImageInitialValue);
    const [showOldPassword, setShowOldPassword] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
    const [toastMessage, setToastMessage] = useState<_ToastMessageProps>(defaultToastData);
    const [showEmailDialog, setShowEmailDialog] = useState<boolean>(false);

    useEffect(() => {
        setProfileImage({
            imageKey: userData?.photoKey ?? null,
            imageUrl: userData?.signedUrl ? userData?.signedUrl : null,
        });
    }, [userData]);

    const handleOpenEmailDialog = () => {
        setShowEmailDialog(true);
    };

    const onProfileImageUpdate = (imageKey: string, imageUrl: string) => {
        const onSuccess = (data: any) => {
            setUserData((prev: any) => ({ ...prev, signedUrl: imageUrl }));
            setToastMessage({
                message: data?.message ?? '',
                variant: AlertVariant.SUCCESS,
                show: true,
            });
        };

        const onError = (error: string, variant: string) => {
            setToastMessage({
                message: error,
                variant,
                show: true,
            });
        };
        updateOrgProfilePicture(changeProfileImageMutation, { staffId: userData?.id!, photoKey: imageKey }, onSuccess, onError);
    };

    const getUserType = (userType: _UserType | null) => {
        switch (userType) {
            case 'adminStaff':
                return translatedText.ADMIN_STAFF;
            case 'superAdmin':
                return translatedText.SUPER_ADMIN;
            case 'organizationAdmin':
                return translatedText.ORG_ADMIN;
            case 'organizationStaff':
                return translatedText.ORG_STAFF;
            default:
                return '';
        }
    };

    // Formik setup
    const resetPasswordForm = useFormik({
        enableReinitialize: true,
        initialValues: { ...initialChangePasswordFormData },
        validationSchema: ChangePasswordSchema,
        onSubmit: (values) => {
            orgStaffChangeNewPassword(
                changePasswordMutation,
                {
                    staffId: userData?.id!,
                    newPassword: values.password,
                    oldPassword: values.oldPassword,
                    confirmPassword: values.confirmPassword,
                },
                (data) => {
                    setToastMessage({
                        message: data?.message ?? '',
                        variant: AlertVariant.SUCCESS,
                        show: true,
                    });
                    resetPasswordForm.resetForm();
                },
                (error: string, variant: string) => {
                    setToastMessage({
                        message: error,
                        variant,
                        show: true,
                    });
                }
            );
        },
    });

    const isFormEmpty =
        !resetPasswordForm.values.oldPassword && !resetPasswordForm.values.password && !resetPasswordForm.values.confirmPassword;

    const passwordCriteria = checkPasswordCriteria(resetPasswordForm.values.password);
    const PasswordRequirement = ({ className, text, isValid }: { className: string; text: string; isValid: boolean }) => {
        let styleClass = 'text-secondary';

        if (resetPasswordForm?.touched?.password && resetPasswordForm?.errors?.password && !isValid) {
            styleClass = 'text-danger';
        }

        if (isValid) {
            styleClass = 'text-success';
        }

        return (
            <div className={`${className} ${styleClass}`}>
                {!isValid ? <i className="ri-close-circle-fill"></i> : <i className="ri-checkbox-circle-fill"></i>}
                <small className="ms-2">{text}</small>
            </div>
        );
    };

    return (
        <>
            <MlxToast
                data-testid="toast"
                message={toastMessage.message}
                onClose={() => setToastMessage(defaultToastData)}
                show={toastMessage.show}
                variant={toastMessage.variant}
            />
            <div className="page-section">
                <span className="page-header">{translatedText.MY_PROFILE}</span>
                <MlxBlockUI className="overflow-auto">
                    <div className="row org-profile-page-container">
                        <div className="col-md-6 pe-0">
                            <div className="page-content org-profile-page-content">
                                <div className="content-header">
                                    <span className="header-title"></span>
                                </div>
                                <div className="content-body">
                                    <MlxImageUpload
                                        mode="basic"
                                        image={profileImage}
                                        setImage={setProfileImage}
                                        validateImageDimension={IMAGE_TYPE['PROFILE_IMAGE']}
                                        cropImage={true}
                                        uploadType="profile-page"
                                        onUploadSuccess={onProfileImageUpdate}
                                        onDeleteSuccess={onProfileImageUpdate}
                                    />
                                    <div className="org-profile-info d-flex flex-column align-items-center">
                                        <span className="user-name-text">
                                            {userData?.firstName} {userData?.lastName}
                                        </span>
                                        <small className="text-muted">{getUserType(userType)}</small>
                                    </div>
                                    <hr className="org-profile-divider" />
                                    <div className="org-profile-info d-flex flex-column align-items-center">
                                        <span className="mb-2">
                                            <i className="pi pi-user me-2"></i>
                                            {userData?.loginUserName}
                                        </span>
                                        <span>
                                            <i className="pi pi-envelope me-2"></i>
                                            {userData?.email}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 pe-0 h-100">
                            <div className="page-content org-profile-page-content mb-2">
                                <div className="content-header">
                                    <span className="header-title">{translatedText.PERSONAL_INFORMATION}</span>
                                </div>
                                <div className="personal-info-content content-body w-100 org-profile-content-body">
                                    <div className="row w-100">
                                        <div className="col-md-6 mb-3 d-flex flex-column">
                                            <label className="mb-1" htmlFor="displayName">
                                                {translatedText.DISPLAY_NAME}
                                            </label>
                                            <InputText
                                                id="displayName"
                                                name="displayName"
                                                className="custom-input-text p-inputtext-sm"
                                                value={userData?.preferredName}
                                                disabled
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3 d-flex flex-column">
                                            <label className="mb-1" htmlFor="hireDate">
                                                {translatedText.HIRE_DATE}
                                            </label>
                                            <InputText
                                                id="hireDate"
                                                name="hireDate"
                                                className={`custom-input-text p-inputtext-sm`}
                                                value={dayjs(userData?.hireDate).format('DD/MM/YYYY') ?? '--'}
                                                disabled
                                            />
                                        </div>
                                    </div>
                                    <div className="row w-100">
                                        <div className="col-md-6 mb-3 d-flex flex-column">
                                            <label className="mb-1" htmlFor="firstName">
                                                {translatedText.FIRST_NAME}
                                            </label>
                                            <InputText
                                                id="firstName"
                                                name="firstName"
                                                className="custom-input-text p-inputtext-sm"
                                                value={userData?.firstName}
                                                disabled
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3 d-flex flex-column">
                                            <label className="mb-1" htmlFor="lastName">
                                                {translatedText.LAST_NAME}
                                            </label>
                                            <InputText
                                                id="lastName"
                                                name="lastName"
                                                className={`custom-input-text p-inputtext-sm`}
                                                value={userData?.lastName}
                                                disabled
                                            />
                                        </div>
                                    </div>
                                    <div className="row w-100">
                                        <div className="col-md-6 mb-3 d-flex flex-column position-relative">
                                            <label className="mb-1" htmlFor="email">
                                                {translatedText.EMAIL}
                                            </label>
                                            <InputText
                                                id="email"
                                                name="email"
                                                className="custom-input-text p-inputtext-sm"
                                                value={userData?.email}
                                                disabled
                                            />
                                            <span
                                                className="position-absolute translate-middle-y me-2"
                                                style={{ cursor: 'pointer', right: '0.75rem', top: '70%' }}
                                                onClick={handleOpenEmailDialog}
                                            >
                                                <i className="ri-pencil-line text-secondary"></i>
                                            </span>
                                        </div>
                                        <div className="col-md-6 mb-3 d-flex flex-column">
                                            <label className="mb-1" htmlFor="phone">
                                                {translatedText.PHONE}
                                            </label>
                                            <InputText
                                                id="phone"
                                                name="phone"
                                                className={`custom-input-text p-inputtext-sm`}
                                                value={userData?.phoneNumber}
                                                disabled
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="page-content org-profile-page-content pe-2">
                                <div className="content-header">
                                    <span className="header-title">{translatedText.RESET_PASSWORD_PROFILE_TITLE}</span>
                                </div>
                                <div className="set-password-form content-body">
                                    <form className="w-100 login-form d-flex flex-column" onSubmit={resetPasswordForm.handleSubmit}>
                                        <div className="w-100 mt-3 position-relative d-flex align-items-center">
                                            <InputText
                                                className={`w-100 custom-input-text p-inputtext-sm ${resetPasswordForm?.touched?.oldPassword && resetPasswordForm?.errors?.oldPassword ? 'border-danger' : ''}`}
                                                type={showOldPassword ? 'text' : 'password'}
                                                value={resetPasswordForm?.values?.oldPassword}
                                                onChange={(event) => {
                                                    resetPasswordForm.setFieldTouched('oldPassword', true);
                                                    resetPasswordForm.setFieldValue('oldPassword', event.target.value, true);
                                                }}
                                                autoComplete="off"
                                                placeholder={translatedText.OLD_PASSWORD}
                                            />
                                            <Button
                                                className="position-absolute end-0"
                                                icon={showOldPassword ? 'ri-eye-fill' : 'ri-eye-off-fill'}
                                                rounded
                                                text
                                                size="large"
                                                severity="secondary"
                                                aria-label="Toggle password visibility"
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    setShowOldPassword(!showOldPassword);
                                                }}
                                                pt={{
                                                    root: {
                                                        style: {
                                                            border: 'none',
                                                            background: 'transparent',
                                                            boxShadow: 'none',
                                                        },
                                                    },
                                                }}
                                            />
                                        </div>
                                        {resetPasswordForm.touched.oldPassword && resetPasswordForm.errors.oldPassword && (
                                            <div className="text-danger small mt-1 ms-1">{resetPasswordForm.errors.oldPassword}</div>
                                        )}
                                        <div className="w-100 mt-3 position-relative d-flex align-items-center">
                                            <InputText
                                                className={`w-100 custom-input-text p-inputtext-sm ${resetPasswordForm?.touched?.password && resetPasswordForm?.errors?.password ? 'border-danger' : ''}`}
                                                type={showPassword ? 'text' : 'password'}
                                                value={resetPasswordForm?.values?.password}
                                                onChange={(event) => {
                                                    resetPasswordForm.setFieldTouched('password', true);
                                                    resetPasswordForm.setFieldValue('password', event.target.value, true);
                                                }}
                                                autoComplete="off"
                                                placeholder={translatedText.NEW_PASSWORD}
                                            />
                                            <Button
                                                className="position-absolute end-0"
                                                icon={showPassword ? 'ri-eye-fill' : 'ri-eye-off-fill'}
                                                rounded
                                                text
                                                size="large"
                                                severity="secondary"
                                                aria-label="Toggle password visibility"
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    setShowPassword(!showPassword);
                                                }}
                                                pt={{
                                                    root: {
                                                        style: {
                                                            border: 'none',
                                                            background: 'transparent',
                                                            boxShadow: 'none',
                                                        },
                                                    },
                                                }}
                                            />
                                        </div>
                                        <div className="w-100 mt-3 position-relative d-flex align-items-center password-requirements">
                                            <div className="row w-80 p-2">
                                                <PasswordRequirement
                                                    className="col-md-6 d-flex align-items-center"
                                                    text={translatedText.PASSWORD_CHECK_LOWERCASE}
                                                    isValid={passwordCriteria.lowercase}
                                                />
                                                <PasswordRequirement
                                                    className="col-md-6 d-flex align-items-center"
                                                    text={translatedText.PASSWORD_CHECK_UPPERCASE}
                                                    isValid={passwordCriteria.uppercase}
                                                />
                                                <PasswordRequirement
                                                    className="col-md-6 d-flex align-items-center"
                                                    text={translatedText.PASSWORD_CHECK_NUMBER}
                                                    isValid={passwordCriteria.number}
                                                />
                                                <PasswordRequirement
                                                    className="col-md-6 d-flex align-items-center"
                                                    text={translatedText.PASSWORD_CHECK_MIN}
                                                    isValid={passwordCriteria.length}
                                                />
                                                <PasswordRequirement
                                                    className="col-md-12 d-flex align-items-center"
                                                    text={translatedText.PASSWORD_CHECK_SPECIAL}
                                                    isValid={passwordCriteria.special}
                                                />
                                            </div>
                                        </div>

                                        <div className="w-100 mt-3 position-relative d-flex align-items-center">
                                            <InputText
                                                className={`w-100 custom-input-text p-inputtext-sm ${resetPasswordForm?.touched?.confirmPassword && resetPasswordForm?.errors?.confirmPassword ? 'border-danger' : ''}`}
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                value={resetPasswordForm?.values?.confirmPassword}
                                                onChange={(event) => {
                                                    resetPasswordForm.setFieldTouched('confirmPassword', true);
                                                    resetPasswordForm.setFieldValue('confirmPassword', event.target.value, true);
                                                }}
                                                autoComplete="new-password"
                                                placeholder={translatedText.CONFIRM_PASSWORD}
                                            />
                                            <Button
                                                className="position-absolute end-0"
                                                icon={showConfirmPassword ? 'ri-eye-fill' : 'ri-eye-off-fill'}
                                                rounded
                                                text
                                                size="large"
                                                severity="secondary"
                                                aria-label="Toggle password visibility"
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    setShowConfirmPassword(!showConfirmPassword);
                                                }}
                                                pt={{
                                                    root: {
                                                        style: {
                                                            border: 'none',
                                                            background: 'transparent',
                                                            boxShadow: 'none',
                                                        },
                                                    },
                                                }}
                                            />
                                        </div>
                                        {resetPasswordForm.touched.confirmPassword && resetPasswordForm.errors.confirmPassword && (
                                            <div className="text-danger small mt-1 ms-1">{resetPasswordForm.errors.confirmPassword}</div>
                                        )}

                                        <div className="w-100 d-flex align-items-center">
                                            <Messages
                                                className="w-100"
                                                ref={messageRef}
                                                pt={{ wrapper: { style: { padding: '0.5em 1rem' } } }}
                                            />
                                        </div>

                                        <div className="password-actions mt-3 w-100 d-flex justify-content-end">
                                            <MlxButton
                                                label={translatedText.CANCEL}
                                                className="me-2"
                                                color=""
                                                raised
                                                icon="ri-close-large-line"
                                                iconPos="right"
                                                size="small"
                                                outlined
                                                type="button"
                                                onClick={() => resetPasswordForm.resetForm()}
                                                disabled={changePasswordMutation.isLoading || isFormEmpty}
                                            />
                                            <MlxButton
                                                label={translatedText.UPDATE}
                                                icon="ri-check-line"
                                                iconPos="right"
                                                size="small"
                                                type="submit"
                                                raised
                                                loading={changePasswordMutation.isLoading}
                                                disabled={changePasswordMutation.isLoading || isFormEmpty}
                                            />
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>{' '}
                </MlxBlockUI>
            </div>

            <ChangeEmailModal
                visible={showEmailDialog}
                onHide={() => setShowEmailDialog(false)}
                userData={userData}
                setToastMessage={setToastMessage}
            />
        </>
    );
}

export default withSidebar(OrgProfile);
